import { createSelector } from '@reduxjs/toolkit';
import merge from 'lodash.merge';

import defaultMetaInfo from '@/constants/defaultMetaInfo';
import { sanitizeHtml } from '@/helpers/utilities';
import { Group } from '@/models/authModels';
import {
  CustomScriptsType,
  CustomTab,
  Customization,
  CustomizationReducerPropsType,
  HeaderCustomization,
  LoginCustomization,
  MetaCustomization,
  PortalCustomizations,
  RegisterCustomization,
  SelectedFormFields,
  SideBarCustomization,
  StorefrontCustomization,
  StorefrontLoginTheme,
} from '@/models/customizationModels';
import { AmbaDropdownItem, ServerStatus } from '@/models/globalModels';

import { RootState } from '../../rootReducer';
import * as t from '../types';

const globalState: CustomizationReducerPropsType = {
  main: {} as Customization,
  customizationStatus: ServerStatus.IDLE,
};

export type PossibleActions =
  | { type: 'CUSTOMIZE_PORTAL'; payload: Customization }
  | { type: 'CUSTOMIZE_PORTAL_ERROR' }
  | { type: 'CUSTOMIZE_META'; payload: MetaCustomization }
  | { type: 'CUSTOMIZE_META_ERROR' }
  | { type: 'CUSTOMIZE_LOGIN'; payload: LoginCustomization }
  | { type: 'CUSTOMIZE_LOGIN_ERROR' }
  | { type: 'CUSTOMIZE_REGISTER'; payload: RegisterCustomization }
  | { type: 'CUSTOMIZE_REGISTER_ERROR' }
  | { type: 'CUSTOMIZE_SIDEBAR_MENU'; payload: SideBarCustomization }
  | { type: 'CUSTOMIZE_SIDEBAR_MENU_ERROR' }
  | { type: 'CUSTOMIZE_HEADER'; payload: HeaderCustomization }
  | { type: 'CUSTOMIZE_HEADER_ERROR' };

const reducer = (state = globalState, action: PossibleActions): CustomizationReducerPropsType => {
  switch (action.type) {
    case t.CUSTOMIZE_PORTAL:
      const custom = Object.assign({}, action.payload);

      return {
        ...state,
        main: custom,
        customizationStatus: ServerStatus.FETCH,
      };
    case t.CUSTOMIZE_PORTAL_ERROR:
      return {
        ...state,
      };
    case t.CUSTOMIZE_META:
      const custMeta = Object.assign({}, action.payload);
      return {
        ...state,
        meta: custMeta,
      };
    case t.CUSTOMIZE_META_ERROR:
      return {
        ...state,
      };
    case t.CUSTOMIZE_LOGIN:
      const custLogin = Object.assign({}, action.payload);
      return {
        ...state,
        login: custLogin,
      };
    case t.CUSTOMIZE_LOGIN_ERROR:
      return {
        ...state,
      };
    case t.CUSTOMIZE_REGISTER:
      const custRegister = Object.assign({}, action.payload);
      return {
        ...state,
        register: custRegister,
      };
    case t.CUSTOMIZE_REGISTER_ERROR:
      return {
        ...state,
      };
    case t.CUSTOMIZE_SIDEBAR_MENU:
      const custSideBarMenu = Object.assign({}, action.payload);
      return {
        ...state,
        sidebar: custSideBarMenu,
      };
    case t.CUSTOMIZE_SIDEBAR_MENU_ERROR:
      return {
        ...state,
      };
    case t.CUSTOMIZE_HEADER:
      const custHeader = Object.assign({}, action.payload);
      return {
        ...state,
        header: custHeader,
      };
    case t.CUSTOMIZE_HEADER_ERROR:
      return {
        ...state,
      };
    default:
      return {
        ...state,
      };
  }
};

// SELECTORS
export const selectMetaCustomization = (state: RootState): MetaCustomization => {
  const custom = state.auth.companyAccount?.customization_uid;
  return custom
    ? {
        head_title: custom.head_title ? custom.head_title : defaultMetaInfo.metaTitle,
        head_description: custom.head_description ? custom.head_description : defaultMetaInfo.metaDescription,
        favicon: custom.favicon ? custom.favicon : defaultMetaInfo.faviconURL,
      }
    : ({} as MetaCustomization);
};

export const selectLoginCustomization = (state: RootState): LoginCustomization => {
  const custom = state.auth.companyAccount?.customization_uid;
  return custom
    ? {
        login_page_logo: custom.login_page_logo,
        login_body_scripts: custom.login_body_scripts,
        login_head_scripts: custom.login_head_scripts,
        button_color: custom.button_color,
        button_text_color: custom.button_text_color,
      }
    : ({} as LoginCustomization);
};

export const selectHeaderCustomization = (state: RootState): HeaderCustomization => {
  const custom = state.auth.companyAccount?.customization_uid;
  const campaign = state.campaigns.selectedCampaign;
  return custom
    ? {
        mobile_banner_image: custom.mobile_banner_image,
        campaign_portal_logo: campaign?.campaign_portal_logo,
        login_page_logo: custom.login_page_logo,
        storefront_portal_logo: custom.storefront_portal_logo,
      }
    : ({} as HeaderCustomization);
};

export const selectPortalCustomizationsCustomization = createSelector(
  (state: RootState) => state.auth.companyAccount?.customization_uid?.portal_customizations,
  (state: RootState) => state.campaigns.selectedCampaign?.portal_customizations,
  // Extract the second argument to pass it on
  (companyCustomizations, campaignCustomizations) => merge({}, defaultPortalCustomizations, companyCustomizations, campaignCustomizations),
);

export const selectCustomScriptsCustomization = createSelector(
  (state: RootState) => state.auth.companyAccount?.customization_uid,
  (state: RootState) => state.campaigns.selectedCampaign?.portal_customizations,
  // Extract the second argument to pass it on
  (companyCustomizations, campaignCustomizations) => {
    const dashboardHeadScripts = campaignCustomizations?.dashboard_head_scripts || companyCustomizations?.dashboard_head_scripts;
    const dashboardBodyScripts = campaignCustomizations?.dashboard_body_scripts || companyCustomizations?.dashboard_body_scripts;

    return {
      dashboard_head_scripts: dashboardHeadScripts,
      dashboard_body_scripts: dashboardBodyScripts,
      login_head_scripts: companyCustomizations?.login_head_scripts,
      login_body_scripts: companyCustomizations?.login_body_scripts,
      registration_head_scripts: companyCustomizations?.registration_head_scripts,
      registration_body_scripts: companyCustomizations?.registration_body_scripts,
    } as CustomScriptsType;
  },
);

export const selectCustomTabs = (state: RootState): CustomTab[] | undefined => {
  const custom = state.auth.companyAccount?.customization_uid;
  return custom?.tabs.filter((i) => i.enabled);
};

const shouldShowMenuItem = (tab: CustomTab, selectedCampaing: number, groups: Group[]) => {
  const legacyCampaignAccess = !!tab.campaigns_with_tab_access.length;
  const campaignCanAccess = selectedCampaing && tab.campaigns_with_tab_access.includes(selectedCampaing); //legacy now we use segments but we keep it for now
  const segmentsCanAccess = !tab.groups.length || (groups && groups.map((group) => group.uid).some((group) => tab.groups.includes(group)));
  return tab.enabled && ((legacyCampaignAccess && campaignCanAccess) || (!legacyCampaignAccess && segmentsCanAccess));
};

export const selectSidebarCustomTabs = createSelector(
  (state: RootState) => state.campaigns.selectedCampaignId,
  (state: RootState) => state.auth.companyAccount?.customization_uid,
  (state: RootState) => state.auth.user.groups,
  (selectedCampaign, custom, groups) =>
    custom?.tabs.filter((i) => {
      const isSidebar = !i.page_element;
      const hasAccess = shouldShowMenuItem(i, selectedCampaign as number, groups);
      return isSidebar && hasAccess;
    }) || [],
);

export const selectAccountCustomTabs = createSelector(
  (state: RootState) => state.campaigns.selectedCampaignId,
  (state: RootState) => state.auth.companyAccount?.customization_uid,
  (state: RootState) => state.auth.user.groups,
  (selectedCampaign, custom, groups) =>
    custom?.tabs.filter((i) => {
      const isAccount = i.page_element === 'account';
      const hasAccess = shouldShowMenuItem(i, selectedCampaign as number, groups);
      return isAccount && hasAccess;
    }),
);

export const selectRegisterCustomization = (state: RootState): RegisterCustomization => {
  const custom = state.auth.companyAccount?.customization_uid;
  const company = state.auth.companyAccount;
  const groups = company?.groups ? company.groups : [];
  const states = company?.ambassador_state_choices ? company.ambassador_state_choices : [];
  const groupOptions = groups?.map((i) => {
    return {
      itemText: i.group_name,
      itemValue: i.group_id,
      itemTextKeyName: i.uid,
      itemCategory: 'groups',
    } as AmbaDropdownItem;
  });
  const stateOptions = states?.map((i) => {
    return {
      itemText: i.name,
      itemValue: i.value,
      itemTextKeyName: i.value,
      itemCategory: 'state',
    } as AmbaDropdownItem;
  });

  return custom && company
    ? {
        ambassador_state_choices: stateOptions,
        ambassador_state_type: company.ambassador_state_type,
        background_color: custom.portal_customizations.register?.background_color || '#fff',
        button_color: custom.button_color,
        button_text_color: custom.button_text_color,
        form_state_label: custom.form_state_label,
        groups: groupOptions,
        mobile_banner_image: custom.mobile_banner_image,
        portal_auto_login: custom.portal_auto_login,
        recaptcha_enabled: company.recaptcha_enabled,
        registration_head_scripts: custom.registration_head_scripts,
        registration_page_button_text: custom.registration_page_button_text,
        registration_page_form_header: sanitizeHtml(custom.registration_page_form_header),
        registration_page_logo: custom.registration_page_logo,
        registration_page_message: sanitizeHtml(custom.registration_page_message),
        registration_page_redirect: custom.registration_page_redirect,
        registration_page_right_column: sanitizeHtml(custom.registration_page_right_column),
        set_contact_loyalty_on_register: custom.set_contact_loyalty_on_register,
        terms_text: sanitizeHtml(custom.terms_text),
      }
    : ({} as RegisterCustomization);
};

export const selectCompanyFormFields = (state: RootState): SelectedFormFields => {
  const custom = state.auth.companyAccount?.customization_uid;

  return custom
    ? {
        form_first_name_position: custom.form_first_name_position,
        form_first_name_required: custom.form_first_name_required,
        form_first_name_checked: custom.form_first_name_checked,
        form_first_name_label: custom.form_first_name_label,
        form_last_name_position: custom.form_last_name_position,
        form_last_name_required: custom.form_last_name_required,
        form_last_name_checked: custom.form_last_name_checked,
        form_last_name_label: custom.form_last_name_label,
        form_email_checked: custom.form_email_checked,
        form_email_position: custom.form_email_position,
        form_email_label: custom.form_email_label,
        form_email_required: custom.form_email_required,
        form_phone_position: custom.form_phone_position,
        form_phone_required: custom.form_phone_required,
        form_phone_checked: custom.form_phone_checked,
        form_phone_label: custom.form_phone_label,
        form_paypal_email_position: custom.form_paypal_email_position,
        form_paypal_email_required: custom.form_paypal_email_required,
        form_paypal_email_checked: custom.form_paypal_email_checked,
        form_paypal_email_label: custom.form_paypal_email_label,
        form_company_position: custom.form_company_position,
        form_company_required: custom.form_company_required,
        form_company_checked: custom.form_company_checked,
        form_company_label: custom.form_company_label,
        form_street_position: custom.form_street_position,
        form_street_required: custom.form_street_required,
        form_street_checked: custom.form_street_checked,
        form_street_label: custom.form_street_label,
        form_city_position: custom.form_city_position,
        form_city_required: custom.form_city_required,
        form_city_checked: custom.form_city_checked,
        form_city_label: custom.form_city_label,
        form_state_position: custom.form_state_position,
        form_state_required: custom.form_state_required,
        form_state_checked: custom.form_state_checked,
        form_state_label: custom.form_state_label,
        form_country_position: custom.form_country_position,
        form_country_required: custom.form_country_required,
        form_country_checked: custom.form_country_checked,
        form_country_label: custom.form_country_label,
        form_groups_position: custom.form_groups_position,
        form_groups_required: custom.form_groups_required,
        form_groups_checked: custom.form_groups_checked,
        form_groups_label: custom.form_groups_label,
        form_zip_position: custom.form_zip_position,
        form_zip_required: custom.form_zip_required,
        form_zip_checked: custom.form_zip_checked,
        form_zip_label: custom.form_zip_label,
        form_job_title_position: custom.form_job_title_position,
        form_job_title_required: custom.form_job_title_required,
        form_job_title_checked: custom.form_job_title_checked,
        form_job_title_label: custom.form_job_title_label,
        form_custom1_position: custom.form_custom1_position,
        form_custom1_required: custom.form_custom1_required,
        form_custom1_checked: custom.form_custom1_checked,
        form_custom1_label: custom.form_custom1_label,
        form_custom2_position: custom.form_custom2_position,
        form_custom2_required: custom.form_custom2_required,
        form_custom2_checked: custom.form_custom2_checked,
        form_custom2_label: custom.form_custom2_label,
        form_custom3_position: custom.form_custom3_position,
        form_custom3_required: custom.form_custom3_required,
        form_custom3_checked: custom.form_custom3_checked,
        form_custom3_label: custom.form_custom3_label,
        form_custom4_position: custom.form_custom4_position,
        form_custom4_required: custom.form_custom4_required,
        form_custom4_checked: custom.form_custom4_checked,
        form_custom4_label: custom.form_custom4_label,
        form_custom5_position: custom.form_custom5_position,
        form_custom5_required: custom.form_custom5_required,
        form_custom5_checked: custom.form_custom5_checked,
        form_custom5_label: custom.form_custom5_label,
        form_custom6_position: custom.form_custom6_position,
        form_custom6_required: custom.form_custom6_required,
        form_custom6_checked: custom.form_custom6_checked,
        form_custom6_label: custom.form_custom6_label,
        form_custom7_position: custom.form_custom7_position,
        form_custom7_required: custom.form_custom7_required,
        form_custom7_checked: custom.form_custom7_checked,
        form_custom7_label: custom.form_custom7_label,
        form_custom8_position: custom.form_custom8_position,
        form_custom8_required: custom.form_custom8_required,
        form_custom8_checked: custom.form_custom8_checked,
        form_custom8_label: custom.form_custom8_label,
        form_custom9_position: custom.form_custom9_position,
        form_custom9_required: custom.form_custom9_required,
        form_custom9_checked: custom.form_custom9_checked,
        form_custom9_label: custom.form_custom9_label,
        form_custom10_position: custom.form_custom10_position,
        form_custom10_required: custom.form_custom10_required,
        form_custom10_checked: custom.form_custom10_checked,
        form_custom10_label: custom.form_custom10_label,
        form_date_of_birth_checked: custom.form_date_of_birth_checked,
        form_date_of_birth_position: custom.form_date_of_birth_position,
        form_date_of_birth_required: custom.form_date_of_birth_required,
        form_date_of_birth_label: custom.form_date_of_birth_label,
      }
    : ({} as SelectedFormFields);
};

export const selectStorefrontCustomization = createSelector(
  (state: RootState) => state.auth.companyAccount?.customization_uid,
  (custom): StorefrontCustomization => {
    const base = defaultPortalCustomizations.storefront;

    if (!custom) {
      // No company customization yet fallback to defaults
      return { ...base };
    }

    const companySf = custom.storefront_customizations || {};
    const merged = merge({}, base, companySf) as StorefrontCustomization;

    // Ensures logo is present
    merged.storefront_portal_logo = custom.storefront_portal_logo || base.storefront_portal_logo;

    return merged;
  },
);

export const selectStorefrontLoginTheme = (state: RootState): StorefrontLoginTheme => {
  const sf = state.auth.companyAccount?.customization_uid?.storefront_customizations;

  const base = defaultPortalCustomizations.storefront.login;
  const login: Partial<StorefrontLoginTheme> = sf?.login || {};

  return {
    backgroundColor: login.backgroundColor || base.backgroundColor,
    loginButtonColor: login.loginButtonColor || base.loginButtonColor,
    loginButtonTextColor: login.loginButtonTextColor || base.loginButtonTextColor,
    iconsColor: login.iconsColor || base.iconsColor,
    fieldsTextColor: login.fieldsTextColor || base.fieldsTextColor,
    fieldsBackgroundColor: login.fieldsBackgroundColor || base.fieldsBackgroundColor,
  };
};

export default reducer;

export const defaultPortalCustomizations: PortalCustomizations = {
  dashboard: {
    show_earned: true,
    show_awaiting_approval: true,
    show_awaiting_payment: true,
    show_paid_out_so_far: true,
    show_points_earned: true,
    show_points_available: true,
    show_points_redeemed: true,
    show_points_expired: true,
    show_cash_available: true,
    show_cash_redeemed: true,
    show_cash_expired: true,
    show_cash_earned: true,
    show_raf: true,
    show_activity_overview_section: true,
    show_tasks_section: true,
    tasks_section_description: '',
    badges_section_description: '',
    show_badges_section: true,
    show_tier_progress: true,
    show_referral_earned_first_event: false,
    show_referral_earned_first_event_text: 'Earned - First Event',
    show_referral_earned_second_event: false,
    show_referral_earned_second_event_text: 'Earned - Second Event',
    show_tasks_description: false,
    show_rewards_cash_earned: false,
    show_rewards_cash_fulfilled: true,
    show_rewards_cash_balance: true,
    show_rewards_cash_redeemed: true,
    show_rewards_cash_expired: true,
    show_rewards_points_earned: false,
    show_rewards_points_fulfilled: true,
    show_rewards_points_balance: true,
    show_rewards_points_redeemed: true,
    show_rewards_points_expired: true,
  },
  stats: {
    show_clicks_graph: true,
    show_shares_graph: true,
    show_referrals_graph: true,
    show_shares_stat: true,
    show_clicks_stat: true,
    show_referrals_stat: true,
  },
  activity: {
    show_referred_name: true,
    show_referred_email: true,
    show_expiration_date: true,
    show_event_data1: true,
    show_event_data2: true,
    show_event_data3: true,
  },
  profile: {
    show_custom1: false,
    show_custom2: false,
    show_custom3: false,
    show_custom4: false,
    show_custom5: false,
    show_custom6: false,
    show_custom7: false,
    show_custom8: false,
    show_custom9: false,
    show_custom10: false,
    show_preferred_payment: false,
    show_paypal_email: false,
  },
  tabs: {
    theme: 'dark',
    show_rewards: true,
    rewards_tab_text: 'Rewards',
    show_sharing: true,
    sharing_tab_text: 'Share Links',
    show_dashboard: true,
    dashboard_tab_text: 'Dashboard',
    show_stats_tab: true,
    stats_tab_text: 'Stats',
    show_assets_tab: true,
    assets_tab_text: 'Assets',
    show_activity_tab: true,
    activity_tab_text: 'Activity',
    show_profile: true,
    show_logout: true,
    link_color: '#3c97d3',
  },
  register: {
    background_color: '#fff',
  },
  login: {
    backgroundColor: '#ffffff',
    loginButtonColor: '#3579a6',
    loginButtonTextColor: '#e49d9d',
    forgotPasswordTextColor: '#428bca',
    iconsColor: '#8994aa',
    fieldsTextColor: '#424d67',
    fieldsBackgroundColor: '#f5f7fc',
  },
  storefront: {
    dashboard: {
      show_points_available: true,
      show_points_redeemed: true,
      show_points_expired: true,
      show_cash_available: true,
      show_cash_redeemed: true,
      show_cash_expired: true,
      show_cash_earned: true,
    },
    tabs: {
      show_activity_tab: true,
      activity_tab_text: 'Activity',
      theme: 'dark',
      link_color: '#3c97d3',
      show_logout: true,
    },
    login: {
      backgroundColor: '#ffffff',
      loginButtonColor: '#3579a6',
      loginButtonTextColor: '#e49d9d',
      iconsColor: '#8994aa',
      fieldsTextColor: '#424d67',
      fieldsBackgroundColor: '#f5f7fc',
    },
    storefront_portal_logo: '',
  },
};
